import {
  Button,
  Card,
  Conditional,
  Input,
  Select,
} from "@result/rui/components";
import { useForm, useNotifications } from "@result/rui/hooks";
import { ApiPage } from "@result/rui/utils/api";
import { upperFirst } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import PageHeader from "#/components/PageHeader.jsx";
import SelectLookupTableItem from "#/components/SelectLookupTableItem";
import BreedingValuesBarChart from "#/components/breeding-values/BreedingValuesBarChart.jsx";
import BreedingValuesLineChart from "#/components/breeding-values/BreedingValuesLineChart.jsx";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect";
import * as DLD from "#/def/data-layer";
import { PLEMENSKA_VREDNOST_KODA } from "#/def/data-layer";
import { SifSpecies } from "#/def/lookup-tables.enums.gen";
import * as LTD from "#/def/lookup-tables.ident.gen";
import useAuthorization from "#/hooks/useAuthorization";
import { useDataLayerQuery } from "#/hooks/useDataLayer";
import { useLookupTableList } from "#/hooks/useLookupTables";
import yesNoOptions from "#/model/YesNoOptions";
import { filterLastnostiPvBySpecies } from "#/pages/breeding-values/BreedingValueUtils";
import BreedingValuesPresets from "#/pages/breeding-values/BreedingValuesPresets";
import KodaIzracunSelect from "#/pages/breeding-values/KodaIzracunSelect";
import { useBreedingValuesBreedQuery } from "#/pages/breeding-values/useBreedingValuesBreedQuery";
import { useBreedingValuesComparisonGeneticTrendQuery } from "#/pages/breeding-values/useBreedingValuesComparisonGeneticTrendQuery";
import * as species from "#/utils/SpeciesScope";

const commonValidation = {
  kodaPopulacije: yup.string().required(),
};

const BreedingValuesComparisonGeneticTrendPage = () => {
  const { t } = useTranslation();
  const supportedSpecies = species.getTranslated();
  const scrollRef = useRef(null);
  const validationSchema = {
    a: yup.object().shape({
      letoOd: yup.number().required(t("validation.required")),
      letoDo: yup
        .number()
        .required(t("validation.required"))
        .min(yup.ref("letoOd"), t("validation.letoDoGteLetoOd"))
        .test("max-50", t("validation.max50YearsDifference"), function (value) {
          const { letoOd } = this.parent;
          return !letoOd || !value || value - letoOd <= 50;
        }),
      ...commonValidation,
    }),
    b: yup.object().shape({
      ...commonValidation,
    }),
  };
  const { addErrorNotification } = useNotifications();

  const getGraphData = (values) => {
    comparisonGeneticTrendQuery
      .query({
        body: {
          a: {
            ...values.a,
            kodaPopulacije:
              values.a.kodaPopulacije === "VSE"
                ? undefined
                : values.a.kodaPopulacije,
            species: supportedSpecies,
          },
          b: {
            ...values.b,
            kodaPopulacije:
              values.b.kodaPopulacije === "VSE"
                ? undefined
                : values.b.kodaPopulacije,
            letoOd: values.a.letoOd,
            letoDo: values.a.letoDo,
            species: supportedSpecies,
            prikazVrednosti: values.a.prikazVrednosti,
            lastnost: values.b.lastnost,
          },
        },
      })
      .catch((e) => {
        console.log("Error fetching breeding values", e);
        addErrorNotification(t("breedingValues.errorFetching"));
      });
  };

  const breedingValueCodeQuery = useDataLayerQuery({
    ident: PLEMENSKA_VREDNOST_KODA,
  });
  const [breedingValueCodes, setBreedingValueCodes] = useState([]);

  const authorization = useAuthorization();

  const distinctSklopi = useMemo(() => {
    return Array.from(new Set(breedingValueCodes.map((bv) => bv.sklop))).filter(
      Boolean
    );
  }, [breedingValueCodes]);

  useEffect(() => {
    breedingValueCodeQuery
      .query({
        page: new ApiPage().page(0).size(1000),
        filter: filterLastnostiPvBySpecies(),
      })
      .then((result) => {
        setBreedingValueCodes(result.content ?? []);
      });
  }, []);

  const breedsQuery = useBreedingValuesBreedQuery();

  useEffect(() => {
    breedsQuery.query({});
  }, []);

  const filteredBreeds = useMemo(() => {
    return (breedsQuery.result?.list ?? []).filter((breed) =>
      supportedSpecies?.includes(breed.species)
    );
  }, [breedsQuery.result, supportedSpecies]);

  const comparisonGeneticTrendQuery =
    useBreedingValuesComparisonGeneticTrendQuery();

  const { getField, submit, values, setValues } = useForm({
    initialValues: {
      a: {
        prikazVrednosti: "PV12",
        kodaPopulacije: "VSE",
        tipPv: "EBV",
        metodaObracuna: "N",
      },
      b: {
        kodaPopulacije: "VSE",
        tipPv: "EBV",
        metodaObracuna: "N",
      },
    },
    schema: validationSchema,
    onSubmit: getGraphData,
  });

  const convertToGraphData = (type, data) => {
    if (data == null) {
      return {};
    }

    return {
      x: [...data.a.map((value) => value.x)],
      y: {
        A: [
          ...data.a.map((value) =>
            value.y.toFixed(graphConfig[type]?.decimalPlaces ?? 0)
          ),
        ],
        B: [
          ...data.b.map((value) =>
            value.y.toFixed(graphConfig[type]?.decimalPlaces ?? 0)
          ),
        ],
      },
      chartType: "LINE",
    };
  };

  useEffect(() => {
    const species = supportedSpecies[0];
    if (species != null) {
      let population = "VSE";
      switch (species) {
        case SifSpecies.KOZA:
          population = "DR";
          break;
        case SifSpecies.OVCA:
          population = "B";
          break;
        case SifSpecies.PRASIC:
          population = "KOO-KP";
          break;
        case SifSpecies.KONJ:
          population = "KAS";
          break;
        case SifSpecies.GOVEDO:
          population = "CI";
          break;
      }
      getField("a.kodaPopulacije").setValue(population);
      getField("b.kodaPopulacije").setValue(population);
    }
  }, [supportedSpecies?.[0]]);

  const graphConfig = useMemo(() => {
    let fatherLabel = "očetov";
    let childLabel = "mladičev";
    switch (supportedSpecies?.[0]) {
      case SifSpecies.KOZA:
      case SifSpecies.OVCA:
        fatherLabel = "ovnov/kozlov";
        childLabel = "jagnjet/kozličev";
        break;
      case SifSpecies.KONJ:
        fatherLabel = "žrebcev";
        childLabel = "žrebet";
        break;
      case SifSpecies.PRASIC:
        fatherLabel = "merjascev";
        childLabel = "pujskov";
        break;
      case SifSpecies.GOVEDO:
        childLabel = "potomcev";
        fatherLabel = "bikov";
        break;
    }
    return {
      odstopanjePovprecjaMladicevGledeNaOceta: {
        type: "BAR",
        title: t(
          "breedingValues.odstopanjePovprecjaMladicevGledeNaOceta.title",
          {
            fatherLabel,
            childLabel,
            interpolation: {
              escapeValue: false,
            },
          }
        ),
        labelX: t("breedingValues.fatherPvClass"),
        labelY: t("breedingValues.descendantDeviation"),
      },
      steviloPotomcevGledeNaRojstvaOcetov: {
        type: "BAR",
        title: t("breedingValues.steviloPotomcevGledeNaRojstvaOcetov.title", {
          fatherLabel,
          childLabel,
          interpolation: {
            escapeValue: false,
          },
        }),
        labelX: t("breedingValues.fatherBirthYear"),
        labelY: t("breedingValues.averageDescendants"),
      },
      steviloZivaliPoLetihRojstva: {
        type: "BAR",
        title: t("breedingValues.steviloZivaliPoLetihRojstva.title"),
        decimalPlaces: 0,
        labelX: t("breedingValues.birthYear"),
        labelY: t("breedingValues.numberOfAnimals"),
      },
      steviloZivaliPoVrednostiRazredaPv: {
        type: "BAR",
        title: t("breedingValues.steviloZivaliPoVrednostiRazredaPv.title"),
        labelX: t("breedingValues.birthYear"),
        labelY: t("breedingValues.numberOfAnimals"),
      },
      trendPvPoLetihRojstva: {
        type: "LINE",
        title: t("breedingValues.trendPvPoLetihRojstva.title"),
        labelX: t("breedingValues.birthYear"),
        labelY: "PV12",
      },
    };
  }, [t, supportedSpecies]);

  const graphData = useMemo(() => {
    const data = comparisonGeneticTrendQuery.result;
    if (data?.trendPvPoLetihRojstva == null) {
      return {};
    }

    const showTrendPV =
      !!getField("a.lastnost").value || !!getField("b.lastnost").value;

    return {
      trendPvPoLetihRojstva: showTrendPV
        ? convertToGraphData(
            "trendPvPoLetihRojstva",
            data.trendPvPoLetihRojstva
          )
        : null,
      steviloZivaliPoLetihRojstva: convertToGraphData(
        "steviloZivaliPoLetihRojstva",
        data.steviloZivaliPoLetihRojstva
      ),
      steviloZivaliPoVrednostiRazredaPv: convertToGraphData(
        "steviloZivaliPoVrednostiRazredaPv",
        data.steviloZivaliPoVrednostiRazredaPv
      ),
      odstopanjePovprecjaMladicevGledeNaOceta: authorization.isKIS
        ? null
        : convertToGraphData(
            "odstopanjePovprecjaMladicevGledeNaOceta",
            data.odstopanjePovprecjaMladicevGledeNaOceta
          ),
      steviloPotomcevGledeNaRojstvaOcetov: authorization.isKIS
        ? null
        : convertToGraphData(
            "steviloPotomcevGledeNaRojstvaOcetov",
            data.steviloPotomcevGledeNaRojstvaOcetov
          ),
    };
  }, [comparisonGeneticTrendQuery.result, authorization.isKIS]);

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        const offset = 70;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = scrollRef.current.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        const startPosition = window.scrollY;
        const distance = offsetPosition - startPosition;
        const duration = 800;
        let start = null;

        function step(timestamp) {
          if (!start) start = timestamp;
          const progress = timestamp - start;
          const percentage = Math.min(progress / duration, 1);

          // Easing function: easeInOutQuad
          const easing =
            percentage < 0.5
              ? 2 * percentage * percentage
              : -1 + (4 - 2 * percentage) * percentage;

          window.scrollTo(0, startPosition + distance * easing);

          if (progress < duration) {
            window.requestAnimationFrame(step);
          }
        }

        window.requestAnimationFrame(step);
      }, 100);
    }
  }, [graphData]);

  return (
    <form className="breeding-values-page" onSubmit={submit}>
      <PageHeader
        title={t("breedingValues.comparisonGeneticTrend")}
        subtitle={t("breedingValues.comparisonGeneticTrend.description")}
        backURL=".."
      />
      <BreedingValuesPresets
        storageKey="BREEDING_VALUES_GENETIC_TREND_PRESETS"
        values={values}
        onLoad={(newValues) => {
          setValues(newValues);
          getGraphData(newValues);
        }}
      />
      <div className="fixed-grid has-1-cols-mobile has-2-cols">
        <div className="grid">
          <GeneticTrendParameters
            getField={getField}
            group="a"
            isLoading={comparisonGeneticTrendQuery.isLoading}
            supportedSpecies={supportedSpecies}
            resetValue={{
              prikazVrednosti: "PV12",
            }}
            distinctSklopi={distinctSklopi}
            breeds={filteredBreeds}
          />
          <GeneticTrendParameters
            getField={getField}
            group="b"
            isLoading={comparisonGeneticTrendQuery.isLoading}
            supportedSpecies={supportedSpecies}
            distinctSklopi={distinctSklopi}
            breeds={filteredBreeds}
          />
        </div>
      </div>
      <div
        className="fixed-grid has-1-cols-mobile has-2-cols-desktop"
        ref={scrollRef}>
        <div className="grid">
          {Object.keys(graphData).map((graphType) => {
            const graph = graphData[graphType];
            const config = graphConfig[graphType] ?? {};
            if (graph?.x == null) {
              return;
            }
            if (config.type === "BAR") {
              return (
                <BreedingValuesBarChart
                  title={config.title}
                  labelX={config.labelX}
                  labelY={config.labelY}
                  data={graph}
                />
              );
            }
            return (
              <BreedingValuesLineChart
                title={graphConfig[graphType]?.title}
                labelX={config.labelX}
                labelY={config.labelY}
                data={graph}
              />
            );
          })}
        </div>
      </div>
    </form>
  );
};

const GeneticTrendParameters = ({
  getField,
  group,
  isLoading,
  resetValue = {},
  breeds,
  distinctSklopi = [],
}) => {
  const { t } = useTranslation();
  const calculationMethods = useLookupTableList(LTD.SifPlemVredMetodaIzracuna);
  const breedingValueTypes = useLookupTableList(LTD.SifPlemVredTipPv);
  const officialityOptions = useMemo(() => yesNoOptions(t), [t]);

  return (
    <Card
      title={`${t("breedingValues.selectParameters")} ${group.toUpperCase()}`}
      className="mb-2">
      <div className="is-flex is-flex-direction-column is-justify-content-space-between">
        <div className="fixed-grid has-1-cols-mobile has-2-cols">
          <div className="grid">
            <SelectLookupTableItem
              ident={LTD.SifPlemVredKodaPopulacije}
              label={t("breedingValues.populationCode")}
              labelType="floating"
              filter={(kodaPopulacije) => kodaPopulacije.sifra !== "VSE"}
              formField={getField(`${group}.kodaPopulacije`)}
            />
            <KodaIzracunSelect
              label={t("breedingValues.calculation")}
              labelType="floating"
              selectionMode="single"
              selectNewest={true}
              isRequired={true}
              formField={getField(`${group}.kodaIzracun`)}
            />
            <SelectLookupTableItem
              ident={LTD.SifSpol}
              labelType="floating"
              formField={getField(`${group}.spol`)}
            />
            <Select
              options={breeds}
              label={t("animal.breed")}
              formField={getField(`${group}.pasme`)}
              optionId="sifra"
              optionValue="sifra"
              labelType="floating"
              selectionMode="multiple"
              optionLabel={(breed) =>
                [breed.sifra, breed.pasma?.naziv].filter(Boolean).join(" - ")
              }
            />
            <Select
              options={distinctSklopi}
              label={t("breedingValues.section")}
              formField={getField(`${group}.sklop`)}
              labelType="floating"
            />
            <DataLayerSearchSelect
              ident={DLD.PLEMENSKA_VREDNOST_KODA}
              queryFields={["sifra", "naziv.sl"]}
              optionId="sifra"
              optionLabel={(koda) =>
                [koda.naziv?.sl, koda.sifra]
                  .filter(Boolean)
                  .join(" - ")
                  .toUpperCase()
              }
              optionValue="sifra"
              filter={() => {
                const filter = filterLastnostiPvBySpecies();
                const currentSklop = getField(`${group}.sklop`).value;
                if (currentSklop != null) {
                  filter.equal("sklop", currentSklop);
                }
                return filter;
              }}
              label={t("breedingValues.breedingValueCodes")}
              formField={getField(`${group}.lastnost`)}
              labelType="floating"
            />
            <Select
              options={officialityOptions}
              optionId="id"
              optionLabel="label"
              optionValue="id"
              label={t("breedingValues.officiality")}
              labelType="floating"
              formField={getField(`${group}.uradnost`)}
            />
            <Conditional condition={group === "a"}>
              <div className="fixed-grid has-2-cols">
                <div className="grid">
                  <Input
                    label={t("breedingValues.yearFrom")}
                    formField={getField(`${group}.letoOd`)}
                    labelType="floating"
                  />
                  <Input
                    label={t("breedingValues.to")}
                    formField={getField(`${group}.letoDo`)}
                    labelType="floating"
                  />
                </div>
              </div>
            </Conditional>
          </div>
        </div>
        <div>
          <span className="label is-inline mr-4">
            {t("breedingValues.type")}
            <span className="has-text-danger is-unselectable"> *</span>
          </span>
          {breedingValueTypes.map((breedingValueType) => (
            <label key={breedingValueType.sifra}>
              <input
                type="radio"
                checked={
                  getField(`${group}.tipPv`).value === breedingValueType.sifra
                }
                onChange={() =>
                  getField(`${group}.tipPv`).setValue(breedingValueType.sifra)
                }
              />
              <span className="px-2">
                {`${upperFirst(breedingValueType.naziv?.sl?.replace("plemenska vrednost", "PV"))}${breedingValueType.naziv?.sl?.toUpperCase()?.startsWith(breedingValueType.sifra.toUpperCase()) ? "" : ` (${breedingValueType.sifra})`}`}
              </span>
            </label>
          ))}
        </div>
        <div>
          <span className="label is-inline mr-4">
            {t("breedingValues.calculationMethods")}
            <span className="has-text-danger is-unselectable"> *</span>
          </span>

          {calculationMethods.map((calculationMethod) => (
            <label key={calculationMethod.sifra}>
              <input
                type="radio"
                checked={
                  getField(`${group}.metodaObracuna`).value ===
                  calculationMethod.sifra
                }
                onChange={() =>
                  getField(`${group}.metodaObracuna`).setValue(
                    calculationMethod.sifra
                  )
                }
              />
              <span className="px-2">
                {upperFirst(
                  calculationMethod?.naziv?.sl ?? calculationMethod?.sifra
                )}
              </span>
            </label>
          ))}
        </div>
        <div className="is-flex is-justify-content-space-between mt-5">
          <Conditional condition={group === "a"}>
            <Button
              label={t("breedingValues.performComparison")}
              isSubmit="true"
              color="primary"
              isLoading={isLoading}
            />
          </Conditional>
          <Conditional condition={group !== "a"}>
            <div></div>
          </Conditional>
          <Button
            label={t("form.reset")}
            onClick={() => getField(group).setValue(resetValue)}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Card>
  );
};
export default BreedingValuesComparisonGeneticTrendPage;
